import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { ProviderInterface } from 'src/app/model/user/provider-interface';
import { BautagebuchService } from '../../services/bautagebuch.service';
import { HeaderServiceService } from 'src/app/services/header-service/header-service.service';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { UserService } from 'src/app/services/user-service/userservice';

import { DataspaceWorkspacePerspective } from '../../models/dataspace-workspace.models';

@Component({
  standalone: false,
  selector: 'app-dataspace-reports-workspace',
  templateUrl: './dataspace-reports-workspace.component.html',
  styleUrls: ['./dataspace-reports-workspace.component.scss']
})
export class DataspaceReportsWorkspaceComponent implements OnInit, OnDestroy {
  perspective: DataspaceWorkspacePerspective = 'nu';
  helpPanelOpened = false;
  initializing = true;
  activeOrganisationId: number | null = null;
  activeOrganisationName = '';
  memberships: OrganisationInterface[] = [];
  selectedOrganisationId: number | null = null;
  organisationSwitchBusy = false;
  demoSeedBusy = false;
  runtimeCleanupBusy = false;
  workspaceRefreshNonce = 0;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderServiceService,
    private readonly organisationService: OrganisationService,
    private readonly userService: UserService,
    private readonly bautagebuchService: BautagebuchService,
    private readonly cd: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.applyActiveOrganisation(this.organisationService.activeOrganisation);

    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        const requestedView = params.get('view');
        this.perspective = requestedView === 'gu' ? 'gu' : requestedView === 'connector' ? 'connector' : 'nu';
        this.cd.detectChanges();
      })
    );

    this.subscriptions.add(
      this.userService.currentMembershipsObserver.subscribe((memberships) => {
        this.memberships = this.sortMemberships(memberships || []);
        this.ensureDefaultActiveOrganisation();
        this.cd.detectChanges();
      })
    );

    this.subscriptions.add(
      this.organisationService.activeItemObserver.subscribe((organisation) => {
        this.applyActiveOrganisation(organisation);
        this.initializing = false;
        this.cd.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setPerspective(perspective: DataspaceWorkspacePerspective): void {
    if (this.perspective === perspective) {
      return;
    }

    if (perspective === 'gu') {
      const guMembership = this.memberships.find((membership) =>
        this.getOrganisationLabel(membership).toLowerCase().includes('pb-40')
      );
      if (guMembership?.organisationId) {
        this.selectedOrganisationId = guMembership.organisationId;
        this.activateSelectedOrganisation();
      }
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: perspective },
      queryParamsHandling: 'merge'
    });
  }

  async activateSelectedOrganisation(): Promise<void> {
    if (!this.selectedOrganisationId || this.organisationSwitchBusy) {
      return;
    }

    if (this.selectedOrganisationId === this.activeOrganisationId) {
      return;
    }

    this.organisationSwitchBusy = true;
    const previousOrganisation = this.organisationService.activeOrganisation;
    const selectedMembership = this.memberships.find((membership) => membership.organisationId === this.selectedOrganisationId) || null;

    // Reflect the selection immediately while the service updates the active organisation.
    if (selectedMembership) {
      this.applyActiveOrganisation(selectedMembership);
      this.syncHeaderProvider(selectedMembership);
      this.cd.detectChanges();
    }

    try {
      await this.organisationService.setActiveOrganisation(this.selectedOrganisationId);
    } catch {
      this.applyActiveOrganisation(previousOrganisation);
      this.syncHeaderProvider(previousOrganisation || null);
    } finally {
      this.organisationSwitchBusy = false;
      this.cd.detectChanges();
    }
  }

  private applyActiveOrganisation(organisation?: OrganisationInterface | null): void {
    this.activeOrganisationId = organisation?.organisationId || null;
    this.activeOrganisationName = organisation?.organisationName || organisation?.name || '';
    this.selectedOrganisationId = this.activeOrganisationId;
  }

  get sortedMemberships(): OrganisationInterface[] {
    return this.memberships;
  }

  get isPlatformAdmin(): boolean {
    return this.userService.isCurrentUserPlatformAdmin();
  }

  seedDemoData(): void {
    if (!this.activeOrganisationId || this.demoSeedBusy || this.runtimeCleanupBusy) {
      return;
    }

    this.demoSeedBusy = true;
    this.bautagebuchService.seedDataspaceDemo(this.activeOrganisationId).subscribe({
      next: (result) => {
        this.demoSeedBusy = false;
        this.snackBar.open('Demo-Seed erfolgreich erzeugt', 'OK', { duration: 4000 });
        this.workspaceRefreshNonce++;
        this.cd.detectChanges();
      },
      error: (error) => {
        this.demoSeedBusy = false;
        this.snackBar.open(error?.message || 'Demo-Daten konnten nicht erzeugt werden.', 'OK', { duration: 6000, panelClass: ['snack-error'] });
        this.cd.detectChanges();
      }
    });
  }

  cleanupRuntimeData(): void {
    if (!this.activeOrganisationId || this.demoSeedBusy || this.runtimeCleanupBusy) {
      return;
    }

    const confirmation = window.prompt(
      'Diese Aktion loescht alle Bautagebuch-Laufzeitdaten. Geben Sie LOESCHEN ein, um fortzufahren.'
    );
    if (confirmation !== 'LOESCHEN') {
      return;
    }

    this.runtimeCleanupBusy = true;
    this.bautagebuchService.cleanupDataspaceRuntime(this.activeOrganisationId).subscribe({
      next: (result) => {
        this.runtimeCleanupBusy = false;
        this.snackBar.open('Runtime-Daten erfolgreich geloescht', 'OK', { duration: 4000 });
        this.workspaceRefreshNonce++;
        this.cd.detectChanges();
      },
      error: (error) => {
        this.runtimeCleanupBusy = false;
        this.snackBar.open(error?.message || 'Runtime-Daten konnten nicht geloescht werden.', 'OK', { duration: 6000, panelClass: ['snack-error'] });
        this.cd.detectChanges();
      }
    });
  }

  private ensureDefaultActiveOrganisation(): void {
    const defaultMembership = this.memberships[0] || null;
    if (!defaultMembership?.organisationId) {
      return;
    }

    if (this.activeOrganisationId === defaultMembership.organisationId) {
      this.selectedOrganisationId = defaultMembership.organisationId;
      return;
    }

    this.selectedOrganisationId = defaultMembership.organisationId;
    this.applyActiveOrganisation(defaultMembership);
    this.syncHeaderProvider(defaultMembership);
    this.organisationService.setActiveOrganisation(defaultMembership.organisationId).then(() => {
      this.applyActiveOrganisation(defaultMembership);
      this.cd.detectChanges();
    }).catch(() => {
      this.selectedOrganisationId = defaultMembership.organisationId;
      this.cd.detectChanges();
    });
  }

  private sortMemberships(memberships: OrganisationInterface[]): OrganisationInterface[] {
    return [...memberships].sort((left, right) =>
      this.getOrganisationLabel(left).localeCompare(this.getOrganisationLabel(right), 'de', { sensitivity: 'base' })
    );
  }

  private getOrganisationLabel(organisation?: OrganisationInterface | null): string {
    return (organisation?.organisationName || organisation?.name || '').trim();
  }

  private syncHeaderProvider(organisation?: OrganisationInterface | null): void {
    if (!organisation?.organisationId) {
      return;
    }

    const providerName = organisation.organisationName || organisation.name || '';
    if (!providerName) {
      return;
    }

    const provider: ProviderInterface = {
      name: providerName,
      img: organisation.organisationPicture,
      organisationId: organisation.organisationId
    };

    this.headerService.setActiveProviderWithoutNavigation(provider);
  }

}
